import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InputType } from '../../constants/inputTypes'
import Icon from '../ui/Icon'
import { Link } from 'react-router-dom'

interface PasswordInputProps {
  id?: string
  name: string
  value: string
  required?: boolean
  label: string
  placeHolder?: string
  disabled?: boolean
  forgotPassword?: boolean
  errorMessage?: string
  onChange: (name: string, value: string, isValid: boolean) => void
  onValidate?: (value: string) => boolean
}

export const PasswordInput = ({
  id,
  name,
  value,
  required = true,
  label,
  placeHolder = '',
  disabled = false,
  forgotPassword = true,
  errorMessage,
  onChange,
  onValidate,
}: PasswordInputProps) => {
  const { t } = useTranslation('inputs')
  const [isError, setIsError] = useState(false)
  const [isTouch, setIsTouch] = useState(false)
  const [isFocus, setIsFocus] = useState(false)
  const [isShow, setIsShow] = useState(false)

  const isRequired = isTouch && required && !value.trim()

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsTouch(true)
      const currentValue = e.target.value
      const hasValue = currentValue.trim().length > 0
      const nativeValid = e.target.validity.valid
      const customValid = onValidate ? onValidate(currentValue) : true
      const valid = hasValue && nativeValid && customValid
      setIsError(hasValue && !valid)
      onChange(name, currentValue, valid)
    },
    [onChange, name, onValidate]
  )

  const fieldClass = [
    'field',
    required && 'required',
    !isFocus && isRequired && 'is-required',
    !isFocus && isError && 'is-error',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={fieldClass}>
      <div className="ph-row ph-between label-row">
        <label htmlFor={id}>{label}</label>
        {forgotPassword && (
          <Link to="/forgot-password" className="mono tiny fly-label-link">
            {t('forgot-password')}
          </Link>
        )}
      </div>
      <div className="input-icon-wrap">
        <input
          id={id}
          className="input"
          type={isShow ? InputType.Text : InputType.Password}
          placeholder={!disabled ? placeHolder : ''}
          required={required}
          disabled={disabled}
          name={name}
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
        />
        <button
          type="button"
          onClick={() => setIsShow(val => !val)}
          className="input-icon-toggle"
          aria-label={t('show-password')}
        >
          <Icon name={isShow ? 'eyeOff' : 'eye'} size={16} />
        </button>
      </div>
      {errorMessage && !isFocus && isError && (
        <div className="field-msg err" role="alert">
          <span className="ico">
            <Icon name="warn" size={13} />
          </span>
          {errorMessage}
        </div>
      )}
    </div>
  )
}
